import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { playAnnouncement } from "@/lib/announcer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Hospital, FilePlus2, Play, CheckCircle2, RotateCcw, User2,
  Activity, NotebookPen, History as HistoryIcon, Trash2, Megaphone,
} from "lucide-react";
import { vitalsSummary, hasAnyVital } from "@/components/VitalsForm";
import ClinicalNotesForm, { EMPTY_NOTES, hasAnyNote } from "@/components/ClinicalNotesForm";
import ReservationsTodayPanel from "@/components/ReservationsTodayPanel";
import { useAuth } from "@/context/AuthContext";

const COLS = [
  { key: "waiting", label: "Waiting", tint: "border-amber-200 bg-amber-50" },
  { key: "in_progress", label: "In Progress", tint: "border-blue-200 bg-blue-50" },
  { key: "done", label: "Done", tint: "border-emerald-200 bg-emerald-50" },
];

function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
}
function fmtDateTime(iso) {
  try {
    return new Date(iso).toLocaleString([], {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
}

export default function DoctorDashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  // Clinical notes are doctor-only on the backend; hide the button for admins viewing this board.
  const isDoctor = Array.isArray(user?.roles) ? user.roles.includes("doctor") : user?.role === "doctor";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // PWA lock: if the app is open as an installed PWA, redirect doctors to
  // the mobile dashboard automatically so it never feels like a browser.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)")?.matches ||
      window.navigator.standalone === true;
    if (isStandalone) nav("/doctor-mobile", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [notesDialog, setNotesDialog] = useState({ open: false, queue: null, value: { ...EMPTY_NOTES }, saving: false });
  const [historyDialog, setHistoryDialog] = useState({ open: false, patient: null, items: [], loading: false });
  const [recordEdit, setRecordEdit] = useState({ open: false, recordId: null, value: { ...EMPTY_NOTES }, saving: false });

  // Open a historical clinical note (from the history dialog) for view + edit
  const openEditRecord = (record) => {
    const cn = record.clinical_notes || {};
    const hasSoap = cn.subjective || cn.objective || cn.assessment || cn.plan || cn.notes;
    const value = hasSoap
      ? { ...EMPTY_NOTES, ...cn }
      : { ...EMPTY_NOTES, assessment: record.title || "", notes: record.content || "" };
    setRecordEdit({ open: true, recordId: record.id, value, saving: false });
  };
  const saveEditRecord = async () => {
    const { recordId, value } = recordEdit;
    if (!recordId) return;
    setRecordEdit((s) => ({ ...s, saving: true }));
    try {
      await api.put(`/clinical-notes/${recordId}`, value);
      toast.success("Clinical note updated");
      setRecordEdit({ open: false, recordId: null, value: { ...EMPTY_NOTES }, saving: false });
      const pid = historyDialog.patient?.id;
      if (pid) {
        const { data } = await api.get(`/patients/${pid}/clinical-notes`);
        setHistoryDialog((s) => ({ ...s, items: data || [] }));
      }
    } catch (e) {
      toast.error(formatErr(e));
      setRecordEdit((s) => ({ ...s, saving: false }));
    }
  };
  const deleteRecordNote = async () => {
    if (!recordEdit.recordId) return;
    if (!window.confirm("Delete this clinical note?\n\nIt will be kept in the audit log.")) return;
    setRecordEdit((s) => ({ ...s, saving: true }));
    try {
      await api.delete(`/clinical-notes/${recordEdit.recordId}`);
      toast.success("Clinical note deleted");
      setRecordEdit({ open: false, recordId: null, value: { ...EMPTY_NOTES }, saving: false });
      const pid = historyDialog.patient?.id;
      if (pid) {
        const { data } = await api.get(`/patients/${pid}/clinical-notes`);
        setHistoryDialog((s) => ({ ...s, items: data || [] }));
      }
    } catch (e) {
      toast.error(formatErr(e));
      setRecordEdit((s) => ({ ...s, saving: false }));
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/queue/doctor/today");
      setItems(data || []);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  const grouped = useMemo(() => {
    const g = { waiting: [], in_progress: [], done: [] };
    items.forEach((q) => {
      // "billing" happens after the consult — keep it visible under Done instead of vanishing.
      const bucket = q.status === "billing" ? "done" : q.status;
      if (g[bucket]) g[bucket].push(q);
    });
    return g;
  }, [items]);

  const setStatus = async (qid, status) => {
    try {
      await api.put(`/queue/${qid}/status`, { status });
      setItems((prev) => prev.map((x) => (x.id === qid ? { ...x, status } : x)));
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  // Call a patient's name: only announces (voice + display). The patient stays in
  // the Waiting lane and can be called repeatedly; use Start to begin the consult.
  const callPatient = async (q) => {
    if (!q) return;
    const name = q.patient_snapshot?.full_name || "patient";
    try {
      const { data } = await api.post(`/queue/${q.id}/announce`, { call_type: "clinic" });
      // Shared player: same voice as every other station, and announced twice.
      await playAnnouncement(data);
      toast.success(`Calling ${name}`);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    }
  };
  const callNext = () => {
    const next = grouped.waiting[0];
    if (!next) { toast.info("No one is waiting"); return; }
    callPatient(next);
  };

  // "Done" -> optionally send a special note to reception
  const [doneDialog, setDoneDialog] = useState({ open: false, queue: null, note: "", saving: false });
  const openDone = (q) => setDoneDialog({ open: true, queue: q, note: "", saving: false });
  const confirmDone = async () => {
    const q = doneDialog.queue;
    if (!q) return;
    setDoneDialog((s) => ({ ...s, saving: true }));
    try {
      await api.put(`/queue/${q.id}/status`, { status: "done" });
      const note = (doneDialog.note || "").trim();
      if (note) {
        await api.post("/reception-notes", { patient_id: q.patient_id, queue_id: q.id, message: note });
      }
      setItems((prev) => prev.map((x) => (x.id === q.id ? { ...x, status: "done" } : x)));
      toast.success(note ? "Marked done — note sent to reception" : "Marked done");
      setDoneDialog({ open: false, queue: null, note: "", saving: false });
    } catch (e) {
      toast.error(formatErr(e));
      setDoneDialog((s) => ({ ...s, saving: false }));
    }
  };

  const openPatient = (q) => nav(`/patients/${q.patient_id}`);
  const createRx = async (q) => {
    if (q.status === "waiting") {
      try { await api.put(`/queue/${q.id}/status`, { status: "in_progress" }); } catch {}
    }
    nav(`/prescriptions/new?patient=${q.patient_id}&returnTo=${encodeURIComponent("/doctor-dashboard")}`);
  };

  // Clinical Notes dialog
  const openNotesDialog = (q) => {
    setNotesDialog({
      open: true,
      queue: q,
      value: { ...EMPTY_NOTES, ...(q.clinical_notes || {}) },
      saving: false,
    });
  };
  const saveNotes = async () => {
    const { queue: q, value } = notesDialog;
    if (!q) return;
    setNotesDialog((s) => ({ ...s, saving: true }));
    try {
      const { data } = await api.put(`/queue/${q.id}/clinical-notes`, value);
      setItems((prev) => prev.map((x) => (x.id === q.id ? data : x)));
      toast.success("Clinical notes saved");
      setNotesDialog({ open: false, queue: null, value: { ...EMPTY_NOTES }, saving: false });
    } catch (e) {
      toast.error(formatErr(e));
      setNotesDialog((s) => ({ ...s, saving: false }));
    }
  };

  // History dialog
  const openHistoryDialog = async (q) => {
    setHistoryDialog({ open: true, patient: { id: q.patient_id, name: q.patient_snapshot?.full_name }, items: [], loading: true });
    try {
      const { data } = await api.get(`/patients/${q.patient_id}/clinical-notes`);
      setHistoryDialog((s) => ({ ...s, items: data || [], loading: false }));
    } catch (e) {
      toast.error(formatErr(e));
      setHistoryDialog((s) => ({ ...s, loading: false }));
    }
  };

  return (
    <div className="p-6 space-y-6">
      <ReservationsTodayPanel />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Hospital className="text-blue-600" /> Doctor Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-1">Today's patients assigned to you.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="btn-refresh-doctor">
          <RotateCcw size={14} className="mr-2" /> Refresh
        </Button>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {COLS.map((c) => (
          <div key={c.key} className={`rounded-lg border ${c.tint} p-3`}>
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-slate-900">{c.label}</div>
              <div className="text-xs text-slate-600">{grouped[c.key].length}</div>
            </div>
            {c.key === "waiting" && (
              <Button
                size="sm"
                onClick={callNext}
                disabled={grouped.waiting.length === 0}
                className="w-full mb-3 bg-blue-700 hover:bg-blue-800"
                data-testid="call-next"
              >
                <Megaphone size={13} className="mr-1" /> Call Next
              </Button>
            )}
            <div className="space-y-3">
              {grouped[c.key].length === 0 && (
                <div className="text-sm text-slate-500 italic">No patients</div>
              )}
              {grouped[c.key].map((q) => (
                <Card key={q.id} className="p-3 bg-white" data-testid={`doc-card-${q.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 truncate">{q.patient_snapshot?.full_name || "—"}</div>
                      <div className="text-xs text-slate-500">
                        {q.patient_snapshot?.age ? `${q.patient_snapshot.age} y/o` : ""} {q.patient_snapshot?.gender || ""}
                        {q.patient_snapshot?.contact_number ? ` • ${q.patient_snapshot.contact_number}` : ""}
                      </div>
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 whitespace-nowrap">{fmtTime(q.created_at)}</div>
                  </div>
                  {q.reason && (
                    <div className="text-sm text-slate-700 mt-2 line-clamp-3">{q.reason}</div>
                  )}

                  {/* Vitals chip */}
                  <div className="mt-2">
                    {hasAnyVital(q.vital_signs) ? (
                      <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 leading-tight flex items-start gap-1" data-testid={`vitals-display-${q.id}`}>
                        <Activity size={12} className="mt-[2px] shrink-0" />
                        <span>{vitalsSummary(q.vital_signs)}</span>
                      </div>
                    ) : (
                      <div className="text-xs text-slate-400 italic">Vitals not recorded</div>
                    )}
                  </div>

                  {/* Notes indicator */}
                  {hasAnyNote(q.clinical_notes) && (
                    <div className="mt-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1 flex items-center gap-1">
                      <NotebookPen size={12} /> Notes saved
                      {q.clinical_notes?.assessment ? <span className="text-slate-600 truncate">— {q.clinical_notes.assessment}</span> : null}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={() => openPatient(q)} data-testid={`view-patient-${q.id}`}>
                      <User2 size={12} className="mr-1" /> Patient
                    </Button>
                    {c.key === "waiting" && (
                      <Button size="sm" onClick={() => callPatient(q)} className="bg-blue-700 hover:bg-blue-800" data-testid={`call-${q.id}`}>
                        <Megaphone size={12} className="mr-1" /> Call Name
                      </Button>
                    )}
                    {c.key === "waiting" && (
                      <Button size="sm" variant="outline" onClick={() => setStatus(q.id, "in_progress")} data-testid={`start-${q.id}`}>
                        <Play size={12} className="mr-1" /> Start
                      </Button>
                    )}
                    {c.key === "in_progress" && (
                      <Button size="sm" variant="outline" onClick={() => setStatus(q.id, "waiting")} data-testid={`back-to-waiting-${q.id}`}>
                        <RotateCcw size={12} className="mr-1" /> Back to Waiting
                      </Button>
                    )}
                    {isDoctor && (
                      <Button size="sm" variant="outline" onClick={() => openNotesDialog(q)} data-testid={`notes-${q.id}`}>
                        <NotebookPen size={12} className="mr-1" /> Clinical Notes
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => openHistoryDialog(q)} data-testid={`notes-history-${q.id}`}>
                      <HistoryIcon size={12} className="mr-1" /> History
                    </Button>
                    <Button size="sm" onClick={() => createRx(q)} data-testid={`createrx-${q.id}`}>
                      <FilePlus2 size={12} className="mr-1" /> Create RX
                    </Button>
                    {c.key !== "done" && (
                      <Button size="sm" variant="outline" onClick={() => openDone(q)} data-testid={`done-${q.id}`}>
                        <CheckCircle2 size={12} className="mr-1" /> Done
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Clinical Notes dialog */}
      <Dialog
        open={notesDialog.open}
        onOpenChange={(open) => !open && setNotesDialog({ open: false, queue: null, value: { ...EMPTY_NOTES }, saving: false })}
      >
        <DialogContent className="max-w-3xl" data-testid="clinical-notes-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <NotebookPen size={16} className="text-blue-600" />
              Clinical Notes — {notesDialog.queue?.patient_snapshot?.full_name || ""}
            </DialogTitle>
          </DialogHeader>
          {/* Vitals reference */}
          {hasAnyVital(notesDialog.queue?.vital_signs) && (
            <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mb-2">
              <Activity size={12} className="inline mr-1" />
              Reception vitals: {vitalsSummary(notesDialog.queue?.vital_signs)}
            </div>
          )}
          <div className="max-h-[60vh] overflow-auto">
            <ClinicalNotesForm
              value={notesDialog.value}
              onChange={(v) => setNotesDialog((s) => ({ ...s, value: v }))}
              idPrefix="dr-notes"
            />
          </div>
          {notesDialog.queue?.clinical_notes?.recorded_by && (
            <div className="text-xs text-slate-500 mt-2">
              Last recorded by {notesDialog.queue.clinical_notes.recorded_by} on {fmtDateTime(notesDialog.queue.clinical_notes.recorded_at)}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setNotesDialog({ open: false, queue: null, value: { ...EMPTY_NOTES }, saving: false })}
              disabled={notesDialog.saving}
            >
              Cancel
            </Button>
            <Button onClick={saveNotes} disabled={notesDialog.saving} data-testid="btn-save-notes">
              {notesDialog.saving ? "Saving..." : "Save Notes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clinical notes history dialog */}
      <Dialog
        open={historyDialog.open}
        onOpenChange={(open) => !open && setHistoryDialog({ open: false, patient: null, items: [], loading: false })}
      >
        <DialogContent className="max-w-3xl" data-testid="notes-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HistoryIcon size={16} />
              Clinical Notes History — {historyDialog.patient?.name || ""}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-auto space-y-2">
            {historyDialog.loading && <div className="text-sm text-slate-500">Loading...</div>}
            {!historyDialog.loading && historyDialog.items.length === 0 && (
              <div className="text-sm text-slate-500 italic">No previous notes.</div>
            )}
            {historyDialog.items.map((it) => {
              const n = it.clinical_notes || {};
              return (
                <div
                  key={it.id}
                  className="border rounded p-3 bg-slate-50 cursor-pointer hover:bg-slate-100 transition"
                  onClick={() => openEditRecord(it)}
                  data-testid={`history-note-${it.id}`}
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="text-xs text-slate-500">
                      {fmtDateTime(it.date)} {it.visit_date ? `• Visit ${it.visit_date}` : ""}
                    </div>
                    <div className="text-xs text-slate-500">by {it.created_by || "—"} · click to edit</div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 text-sm">
                    {n.subjective && <div><span className="font-semibold text-slate-700">S:</span> {n.subjective}</div>}
                    {n.objective && <div><span className="font-semibold text-slate-700">O:</span> {n.objective}</div>}
                    {n.assessment && <div><span className="font-semibold text-slate-700">A:</span> {n.assessment}</div>}
                    {n.plan && <div><span className="font-semibold text-slate-700">P:</span> {n.plan}</div>}
                    {n.notes && <div className="md:col-span-2"><span className="font-semibold text-slate-700">Notes:</span> {n.notes}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Mark Done + optional note to reception */}
      <Dialog
        open={doneDialog.open}
        onOpenChange={(open) => !open && setDoneDialog({ open: false, queue: null, note: "", saving: false })}
      >
        <DialogContent className="max-w-lg" data-testid="done-note-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 size={16} className="text-emerald-600" />
              Mark Done — {doneDialog.queue?.patient_snapshot?.full_name || ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm text-slate-600">Special note to reception (optional)</label>
            <textarea
              rows={4}
              value={doneDialog.note}
              onChange={(e) => setDoneDialog((s) => ({ ...s, note: e.target.value }))}
              placeholder="e.g. Schedule follow-up in 2 weeks · collect lab results · advise fasting for next visit"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              data-testid="done-note-text"
            />
            <p className="text-xs text-slate-400">If you add a note, it pops up on the reception screen and is kept in their inbox.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDoneDialog({ open: false, queue: null, note: "", saving: false })} disabled={doneDialog.saving}>Cancel</Button>
            <Button onClick={confirmDone} disabled={doneDialog.saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-confirm-done">
              {doneDialog.saving ? "Saving..." : (doneDialog.note.trim() ? "Done + Send Note" : "Mark Done")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit historical clinical note */}
      <Dialog
        open={recordEdit.open}
        onOpenChange={(open) => !open && setRecordEdit({ open: false, recordId: null, value: { ...EMPTY_NOTES }, saving: false })}
      >
        <DialogContent className="max-w-3xl" data-testid="edit-history-note-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <NotebookPen size={16} className="text-blue-600" /> Edit Clinical Note
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto">
            <ClinicalNotesForm
              value={recordEdit.value}
              onChange={(v) => setRecordEdit((s) => ({ ...s, value: v }))}
              idPrefix="edit-note"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={deleteRecordNote}
              disabled={recordEdit.saving}
              className="mr-auto text-red-600 border-red-200 hover:bg-red-50"
              data-testid="btn-delete-history-note"
            >
              <Trash2 size={14} className="mr-1" /> Delete
            </Button>
            <Button
              variant="outline"
              onClick={() => setRecordEdit({ open: false, recordId: null, value: { ...EMPTY_NOTES }, saving: false })}
              disabled={recordEdit.saving}
            >
              Cancel
            </Button>
            <Button onClick={saveEditRecord} disabled={recordEdit.saving} data-testid="btn-save-history-note">
              {recordEdit.saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
